import { RefObject } from "react";
import { useTranslation } from "react-i18next";

import DeleteIcon from "src/core/assets/images/icons/delete.svg?react";
import EditIcon from "src/core/assets/images/icons/edit.svg?react";
import PlusButton from "src/core/assets/images/icons/plus.svg?react";
import { Spinner } from "src/core/components/custom-ui/spinner";
import { TruncatedTextTooltip } from "src/core/components/custom-ui/truncated-text-tooltip";
import { DeleteDiligenceModal } from "src/core/components/folder/diligences/DeleteDiligenceModal";
import { FolderDiligencesReadOnly } from "src/core/components/folder/diligences/FolderDiligencesReadOnly";
import UpsertDiligenceModal from "src/core/components/folder/diligences/UpsertDiligenceModal";
import { Alert, AlertDescription } from "src/core/components/ui/alert";
import { Button } from "src/core/components/ui/button";
import { Checkbox } from "src/core/components/ui/checkbox";
import { Status } from "src/core/constants/folders";
import { Diligence } from "src/core/gql/graphql";
import { useFolderDiligences } from "src/core/hooks/folder/useFolderDiligences";
import { cn } from "src/core/lib/utils";

type FolderDiligencesProps = {
  diligencesListRef: RefObject<HTMLElement>;
};

export const FolderDiligences = ({ diligencesListRef }: FolderDiligencesProps) => {
  const { t } = useTranslation();
  const {
    diligences,
    isDilgencesClicked,
    prepareDiligence,
    handleDiligenceChange,
    handleEditDiligence,
    handleAddDiligence,
    handleDeleteDiligence,
    loadingDiligenceId,
    isLoading,
    isReadOnly,
    isDiligenceReady,
    openUpsertModal,
    setOpenUpsertModal,
    diligenceToEdit,
    setDiligenceToEdit,
    openDeleteModal,
    setOpenDeleteModal,
    isDiligencesCreated,
    folderState,
  } = useFolderDiligences(diligencesListRef);

  if (
    folderState === Status.VALIDATED ||
    folderState === Status.REJECTED ||
    folderState === Status.VALIDATION_PENDING ||
    folderState === Status.VALIDATION_PENDING_HR
  ) {
    return <FolderDiligencesReadOnly folderState={folderState} />;
  }

  return (
    <div>
      {diligences.length > 0 || (isDiligencesCreated && !isLoading) ? (
        <div className="flex flex-col gap-y-10">
          {diligences.length > 0 && (
            <ul className="folder-diligences-data-one-page">
              {diligences.map((diligence) => (
                <li key={diligence.id} className="folder-diligences-data-item group">
                  {loadingDiligenceId === diligence.id ? (
                    <div className="h-checkbox w-checkbox flex items-center justify-center">
                      <Spinner size="sm" />
                    </div>
                  ) : (
                    <Checkbox
                      className="text-blue-primary border-blue-primary"
                      onCheckedChange={(checked) => {
                        handleDiligenceChange(diligence.id, diligence.name, Boolean(checked));
                      }}
                      checked={Boolean(diligence.selected)}
                      disabled={isReadOnly}
                    />
                  )}
                  <TruncatedTextTooltip>{diligence.name}</TruncatedTextTooltip>
                  {!isReadOnly && (
                    <div className="flex flex-row items-center gap-x-10 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="action"
                        size="icon"
                        onClick={() => handleEditDiligence(diligence as Diligence)}
                        disabled={loadingDiligenceId === diligence.id}
                      >
                        <EditIcon />
                      </Button>
                      <Button
                        variant="action"
                        size="icon"
                        onClick={() => handleDeleteDiligence(diligence as Diligence)}
                        disabled={loadingDiligenceId === diligence.id}
                      >
                        <DeleteIcon />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {diligences.length === 0 && isDiligencesCreated && !isLoading && (
            <div className="text-base font-light text-muted-foreground ml-30">
              {t("folder.diligences.noDiligencesCreated")}
            </div>
          )}
          {!isReadOnly && (
            <Button className="w-fit" variant="default" onClick={() => handleAddDiligence()}>
              <PlusButton />
              {t("folder.diligences.addDiligence")}
            </Button>
          )}
          <UpsertDiligenceModal
            open={openUpsertModal}
            setOpen={setOpenUpsertModal}
            diligence={diligenceToEdit}
            setDiligenceToEdit={setDiligenceToEdit}
          />
          <DeleteDiligenceModal
            open={openDeleteModal}
            setOpen={setOpenDeleteModal}
            diligence={diligenceToEdit}
            setDiligenceToEdit={setDiligenceToEdit}
          />
        </div>
      ) : (
        <Alert
          variant={isDilgencesClicked && !isDiligenceReady ? "destructive" : "default"}
          className={cn(
            "h-50 flex items-center w-full px-20 mx-0 font-light ",
            !isDilgencesClicked || isDiligenceReady ? "bg-background-secondary" : "",
            isDiligenceReady && "border-2 border-dashed border-orange-medium"
          )}
        >
          <AlertDescription className="w-full">
            <div className="flex flex-row justify-between items-center w-full">
              <div className="flex flex-row items-center">
                {isDiligenceReady
                  ? t("folder.diligences.diligenceReady")
                  : isDilgencesClicked
                  ? t("folder.diligences.noDiligenceInfoError")
                  : t("folder.diligences.noDiligenceInfo")}
              </div>

              <Button
                variant="outline"
                className="px-15"
                size="small"
                onClick={prepareDiligence}
                loading={isLoading}
                disabled={isReadOnly}
              >
                {t("folder.diligences.prepareDiligence")}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
